// @ts-check
/**
 * Movement since the previous position, one line each. The dot colour follows the direction the report
 * supplies; it says nothing about how large the change was.
 */
import React from 'react';
import PropTypes from 'prop-types';

import { TestableText } from '@components/general/TestableComponents';
import { projectTitle } from '@datahooks/ProjectsListContext';
import { renderReportMarkdown } from './AIReportMarkdown.jsx';

/** @typedef {import('@datahooks/types/organisationAiReport.types.js').AiReportSignificantChange} AiReportSignificantChange */
/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

/** Matches the Organisation Overview health matrix, so the same movement reads the same colour twice. */
const DIRECTION_DOT = {
  deteriorated: 'bg-red-500',
  improved: 'bg-green-500',
  incomplete: 'bg-orange-400',
};

/** The dot is the only carrier of direction, so its meaning is also given as text. */
const DIRECTION_LABEL = {
  deteriorated: 'Deteriorated',
  improved: 'Improved',
  incomplete: 'Incomplete',
};

/**
 * Renders the list of significant changes for projects within an AI report.
 * 
 * @param {{ changes: AiReportSignificantChange[], projects?: BasicProject[] }} props
 * @returns {JSX.Element|null}
 */
const AIReportChanges = ({ changes = [], projects = [] }) => {
  if (changes.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-4" data-testid="ai-report-changes">
      {changes.map((change, index) => (
        <li
          key={`${change.projectId}-${change.changeType}-${index}`}
          className="flex items-start gap-3"
          data-testid={`ai-report-change-${index}`}
        >
          <span
            aria-hidden="true"
            className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${DIRECTION_DOT[change.direction] ?? 'bg-gray-300'}`}
            data-testid={`ai-report-change-${index}-dot`}
          />
          <div className="min-w-0">
            {projectTitle(projects, change.projectId, change.projectName) && (
              <TestableText
                variant="small"
                className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                dataTestId={`ai-report-change-${index}-project`}
              >
                {projectTitle(projects, change.projectId, change.projectName)}
              </TestableText>
            )}
            <TestableText variant="small" className="text-sm font-semibold text-gray-900 dark:text-gray-100" dataTestId={`ai-report-change-${index}-headline`}>
              <span className="sr-only">{`${DIRECTION_LABEL[change.direction] ?? 'Changed'}: `}</span>
              {change.headline}
            </TestableText>
            <div
              className="mt-1 space-y-2 text-sm leading-6 text-gray-600 dark:text-gray-400"
              data-testid={`ai-report-change-${index}-detail`}
            >
              {renderReportMarkdown(change.detail, `ai-report-change-${index}-detail`)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

AIReportChanges.propTypes = {
  changes: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    projectName: PropTypes.string,
    direction: PropTypes.oneOf(['deteriorated', 'improved', 'incomplete']),
    changeType: PropTypes.string,
    headline: PropTypes.string.isRequired,
    detail: PropTypes.string,
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIReportChanges;
